import path from 'path';
import fs from 'fs-extra';
import chalk from 'chalk';

interface IInitOptions {
  name?: string;
  template?: 'react' | 'node' | 'express';
}

/**
 * Init 명령어 - 새로운 프로젝트 초기화
 */
export async function initCommand(options: IInitOptions): Promise<void> {
  try {
    console.log(chalk.bold.blue('🚀 Debug Time Machine 프로젝트 초기화'));
    console.log('');

    // 프로젝트 이름 (기본값 사용)
    const projectName = options.name || 'debug-time-machine-project';
    const template = options.template || 'react';
    
    console.log(`📁 프로젝트 이름: ${chalk.cyan(projectName)}`);
    console.log(`📋 템플릿: ${chalk.cyan(template)}`);
    console.log('');

    // 프로젝트 디렉토리 생성
    const projectPath = path.join(process.cwd(), projectName);
    
    if (await fs.pathExists(projectPath)) {
      console.error(chalk.red(`❌ 디렉토리가 이미 존재합니다: ${projectPath}`));
      return;
    }

    console.log(chalk.blue('📦 프로젝트 생성 중...'));

    // 프로젝트 디렉토리 생성
    await fs.ensureDir(projectPath);

    // 템플릿 파일들 생성
    await createTemplateFiles(projectPath, projectName, template);

    console.log(chalk.green('✅ 프로젝트가 성공적으로 생성되었습니다!'));
    console.log('');
    console.log(chalk.bold('다음 단계:'));
    console.log(`  ${chalk.cyan('cd')} ${projectName}`);
    console.log(`  ${chalk.cyan('npm install')}`);
    console.log(`  ${chalk.cyan('npm run debug')}`);
    console.log('');

  } catch (error) {
    console.error(chalk.red('❌ 프로젝트 생성 중 오류가 발생했습니다:'));
    console.error(error instanceof Error ? error.message : '알 수 없는 오류');
  }
}

/**
 * 템플릿 파일들 생성
 */
async function createTemplateFiles(projectPath: string, projectName: string, template: string): Promise<void> {
  // package.json 생성
  const packageJson = {
    name: projectName,
    version: '1.0.0',
    description: 'Debug Time Machine 프로젝트',
    scripts: {
      dev: template === 'react' ? 'vite --port 3000' : 'node index.js',
      debug: `debug-time-machine start-with-app '${template === 'react' ? 'vite --port 3000' : 'node index.js'}'`,
      build: template === 'react' ? 'vite build' : 'echo "Build complete"',
    },
    dependencies: {
      'debug-time-machine-react-client': '^1.0.0',
      ...(template === 'react' ? {
        react: '^18.3.1',
        'react-dom': '^18.3.1',
      } : {}),
    },
    devDependencies: {
      ...(template === 'react' ? {
        '@types/react': '^18.3.1',
        '@types/react-dom': '^18.3.0',
        '@vitejs/plugin-react': '^4.2.1',
        typescript: '^5.4.5',
        vite: '^5.2.8',
      } : {}),
    },
  };

  await fs.writeJson(path.join(projectPath, 'package.json'), packageJson, { spaces: 2 });

  if (template === 'react') {
    // React 템플릿 파일들
    await createReactTemplate(projectPath);
  } else {
    // Node.js 템플릿 파일들
    await createNodeTemplate(projectPath);
  }

  // README.md 생성
  const readme = `# ${projectName}

Debug Time Machine 프로젝트입니다.

## 시작하기

\`\`\`bash
npm install
npm run debug  # Debug Time Machine과 함께 실행
\`\`\`

## 일반 실행

\`\`\`bash
npm run dev    # 일반 실행
\`\`\`
`;

  await fs.writeFile(path.join(projectPath, 'README.md'), readme);
}

/**
 * React 템플릿 생성
 */
async function createReactTemplate(projectPath: string): Promise<void> {
  // index.html
  const indexHtml = `<!DOCTYPE html>
<html lang="en">
  <head>
    <meta charset="UTF-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1.0" />
    <title>Debug Time Machine App</title>
  </head>
  <body>
    <div id="root"></div>
    <script type="module" src="/src/main.tsx"></script>
  </body>
</html>
`;

  await fs.writeFile(path.join(projectPath, 'index.html'), indexHtml);

  // vite.config.ts
  const viteConfig = `import { defineConfig } from 'vite'
import react from '@vitejs/plugin-react'

export default defineConfig({
  plugins: [react()],
})
`;

  await fs.writeFile(path.join(projectPath, 'vite.config.ts'), viteConfig);

  // tsconfig.json
  const tsConfig = {
    compilerOptions: {
      target: 'ES2020',
      lib: ['ES2020', 'DOM', 'DOM.Iterable'],
      module: 'ESNext',
      skipLibCheck: true,
      moduleResolution: 'bundler',
      allowImportingTsExtensions: true,
      resolveJsonModule: true,
      isolatedModules: true,
      noEmit: true,
      jsx: 'react-jsx',
      strict: true,
      noUnusedLocals: true,
      noUnusedParameters: true,
      noFallthroughCasesInSwitch: true,
    },
    include: ['src'],
  };

  await fs.writeJson(path.join(projectPath, 'tsconfig.json'), tsConfig, { spaces: 2 });

  // src 디렉토리 생성
  const srcPath = path.join(projectPath, 'src');
  await fs.ensureDir(srcPath);

  // main.tsx
  const mainTsx = `import React from 'react'
import ReactDOM from 'react-dom/client'
import App from './App.tsx'

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <App />
  </React.StrictMode>,
)
`;

  await fs.writeFile(path.join(srcPath, 'main.tsx'), mainTsx);

  // App.tsx
  const appTsx = `import { useState } from 'react'
import { useDebugTimeMachine } from 'debug-time-machine-react-client'

function App() {
  // Debug Time Machine Hook 추가
  useDebugTimeMachine({ autoConnect: true });

  const [count, setCount] = useState(0)

  return (
    <div style={{ padding: '2rem', fontFamily: 'Arial, sans-serif' }}>
      <h1>🕰️ Debug Time Machine App</h1>
      <p>Debug Time Machine이 설정된 React 앱입니다.</p>
      
      <div style={{ margin: '2rem 0' }}>
        <h2>카운터: {count}</h2>
        <button 
          onClick={() => setCount(count + 1)}
          style={{ margin: '0 10px', padding: '10px 20px' }}
        >
          증가 (+)
        </button>
        <button 
          onClick={() => setCount(count - 1)}
          style={{ margin: '0 10px', padding: '10px 20px' }}
        >
          감소 (-)
        </button>
      </div>

      <div style={{ background: '#f0f0f0', padding: '1rem', borderRadius: '8px' }}>
        <h3>🐛 Debug 모드 실행 방법:</h3>
        <code>npm run debug</code>
        <p>위 명령어로 실행하면 Debug Time Machine이 자동으로 시작됩니다!</p>
      </div>
    </div>
  )
}

export default App
`;

  await fs.writeFile(path.join(srcPath, 'App.tsx'), appTsx);
}

/**
 * Node.js 템플릿 생성
 */
async function createNodeTemplate(projectPath: string): Promise<void> {
  const indexJs = `const { useDebugTimeMachine } = require('debug-time-machine-react-client');

console.log('🕰️ Debug Time Machine Node.js App');
console.log('Debug Time Machine이 설정된 Node.js 앱입니다.');

// 간단한 HTTP 서버
const http = require('http');

const server = http.createServer((req, res) => {
  res.writeHead(200, { 'Content-Type': 'text/html; charset=utf-8' });
  res.end(\`
    <h1>🕰️ Debug Time Machine Node.js App</h1>
    <p>Debug Time Machine이 설정된 Node.js 서버입니다.</p>
    <p>포트: 3000</p>
  \`);
});

server.listen(3000, () => {
  console.log('서버가 포트 3000에서 실행 중입니다.');
  console.log('http://localhost:3000');
});
`;

  await fs.writeFile(path.join(projectPath, 'index.js'), indexJs);
}
